import {Grid, Row} from "react-bootstrap"
import React, {Component} from "react"
import {get} from "lodash"

import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import userService from "@irati/identity/services/users"
import {User, Team, TeamRegionMap} from "@irati/types"
import {QueryUsersArgs} from "@irati/identity/services/queries/usersForBusiness"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import {Select, Checkbox, Radio, ToolTipRenderer} from "@irati/ui/src/FormItem"
import teamService from "@irati/identity/services/teams"
import authentication from "@irati/auth"
import Spacing from "@irati/ui/src/Spacing"
import adminTracking from "@irati-tracking/admin"
import {handleActivationStatusChange} from "@irati/identity/apollo/query/UsersForBusiness"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import Validation from "@irati/utils/validation"
import BURegion from "@irati-web/fields/team/BURegion"
import buService from "@irati/billing/services/bu"
import {Label, H3} from "@irati/ui/src/Typography"
import {Para} from "@irati/ui/src/Labels"
import SideBarContext from "@irati/ui/src/AppContainer/Context/SideBarContext"
import theme from "@irati/ui/src/global/theme"
import teamName from "@shared/core/utils/team/teamName"

import {AreYouSurePopUp} from "../../AreYouSurePopUp/index"
import {PopUp} from "./styled/styled"
import MoveUsersModal from "./MoveUsersModal"
import MoveUsersSuccessModal from "./MoveUsersSuccessModal"

const MANAGE_USERS_BAR_SOURCE = "People - Multi-select"

interface Props extends InjectIntl {
  onSave: () => void,
  selectedEmployees: User[],
  variables: QueryUsersArgs,
  teamId?: string
}

interface State {
  load: Promise<any> | null,
  showMoveTeamModal: boolean,
  showMoveTeamSuccessModal: boolean,
  team?: string,
  teams?: Team[],
  submit?: Promise<any> | null,
  usersAlreadyInTeam?: User[],
  usersToAdd?: User[],
  successfullyUpdatedEmployees: number,
  showMoveBusinessUnitModal: boolean,
  showMoveBusinessUnitSuccessModal: boolean,
  businessUnitConfirmation: boolean,
  businessUnit?: string,
  businessUnits?: Team[],
  isPrimaryTeamSelected: boolean,
  isCountryNew: boolean,
  teamRegions: TeamRegionMap[]
}

class ManageUsersBottomBar extends Component<Props, State> {
  static defaultProps = {
    employeesLength: 0
  }

  state: State = {
    load: null,
    showMoveTeamModal: false,
    showMoveTeamSuccessModal: false,
    team: undefined,
    teams: [],
    submit: null,
    successfullyUpdatedEmployees: 0,
    showMoveBusinessUnitModal: false,
    showMoveBusinessUnitSuccessModal: false,
    businessUnitConfirmation: false,
    isPrimaryTeamSelected: false,
    isCountryNew: false,
    teamRegions: []
  }

  componentDidMount() {
    teamService.list(authentication.business().uuid).then((teams) => {
      return officesForTenant().then((businessUnits) => {
        return this.setState({
          teams,
          businessUnits
        })
      })
    })
    buService.list({businessId: authentication.business().uuid}, {fetchPolicy: "network-only"}).then((teamRegions) => {
      this.setState({teamRegions})
    })
  }

  openDeactivatePopUp = () => {
    adminTracking.deativate()
    this.deactivatePopup.toggle()
  }

  isDeactivatedList = () => {
    return !get(this.props.selectedEmployees, "[0].isEnabled", false)
  }

  areYouSureText = () => {
    const {selectedEmployees, formatMessage} = this.props
    const activateThem = this.isDeactivatedList()
    const name = selectedEmployees[0]
      ? selectedEmployees[0].firstName
      : formatMessage({id: "admin.components.manageUsersPopUp.areYouSureText.thisUser"})

    const action = activateThem
      ? formatMessage({id: "admin.components.manageUsersPopUp.areYouSureText.action.activate"})
      : formatMessage({id: "admin.components.manageUsersPopUp.areYouSureText.action.deactivate"})
    const to =
      selectedEmployees.length > 1
        ? formatMessage({id: "admin.components.manageUsersPopUp.areYouSureText.users"}, {selectedEmployeesLength: selectedEmployees.length})
        : name
    return formatMessage({id: "admin.components.manageUsersPopUp.areYouSureText.text"}, {action, to})
  }

  handleSubmit = () => {
    adminTracking.deactivateConfirmed()
    const {selectedEmployees, onSave} = this.props
    const isDeactivatedList = this.isDeactivatedList()
    const request = isDeactivatedList ? userService.activate : userService.deactivate
    const load = Promise.all(
      selectedEmployees.map((user) => {
        return request(user).then(() => {
          return userService.get(user.uuid).then((updatedUser) => handleActivationStatusChange(this.props.variables, updatedUser))
        })
      })
    ).then(onSave)

    this.setState({load})

    return load
  }

  handleMoveTeamClick = () => {
    adminTracking.assignTeams()
    this.toggleMoveTeamModal()
  }

  toggleMoveTeamModal = () => {
    this.setState(({showMoveTeamModal}) => ({
      showMoveTeamModal: !showMoveTeamModal
    }))
  }

  toggleMoveTeamSuccessModal = () => {
    this.setState(({showMoveTeamSuccessModal}) => ({
      showMoveTeamSuccessModal: !showMoveTeamSuccessModal
    }))
  }

  toggleMoveBusinessUnitModal = () => {
    this.setState(({showMoveBusinessUnitModal}) => ({
      showMoveBusinessUnitModal: !showMoveBusinessUnitModal,
      submit: null
    }))
  }

  toggleMoveBusinessUnitSuccessModal = () => {
    this.setState(({showMoveBusinessUnitSuccessModal}) => ({
      showMoveBusinessUnitSuccessModal: !showMoveBusinessUnitSuccessModal,
      submit: null
    }))
  }

  toggleBusinessUnitConfirmation = () => {
    this.setState(({businessUnitConfirmation}) => ({
      businessUnitConfirmation: !businessUnitConfirmation
    }))
  }

  handleMoveTeam = () => {
    const {onSave, selectedEmployees} = this.props
    const {team, isPrimaryTeamSelected} = this.state

    if (isPrimaryTeamSelected) {
      adminTracking.assignTeamsSetPrimaryTeam()
    } else {
      adminTracking.assignTeamsSetOtherTeam()
    }
    adminTracking.assignTeamsConfirmed()

    const usersAlreadyInTeam = selectedEmployees.filter((user) => {
      if (isPrimaryTeamSelected) {
        return user.primaryTeamId === team
      }
      return user.teamIds.includes(team)
    })
    const usersToAdd = selectedEmployees.filter((user) => {
      if (isPrimaryTeamSelected) {
        return !(user.primaryTeamId === team)
      }
      return !user.teamIds.includes(team)
    })

    const submit = Promise.all(
      usersToAdd.map((user) => teamService.addUserToTeam(team, user.uuid))
    ).then((res) => {
      if (!isPrimaryTeamSelected) { return res }
      return Promise.all(
        usersToAdd.map((user) => userService.update({user: {uuid: user.uuid, primaryTeamId: team}}))
      )
    }).then((res) => {
      this.setState({successfullyUpdatedEmployees: res?.length})
      onSave()
      this.toggleMoveTeamModal()
      this.toggleMoveTeamSuccessModal()
    })
    this.setState({submit, usersAlreadyInTeam, usersToAdd})
  }

  handleMoveBusinessUnit = () => {
    const {onSave, selectedEmployees} = this.props
    const {businessUnit, isCountryNew} = this.state
    let businessUnits = [...this.state.businessUnits]

    const submit = new Promise(async() => {
      if (isCountryNew) {
        await buService.createForRegion({businessId: authentication.business().uuid, regionId: businessUnit}).then(({team}) => {
          adminTracking.adminPeopleConfirmAddCountry(MANAGE_USERS_BAR_SOURCE)
          businessUnits = [...businessUnits, team]
        })
      }
      return buService. getForRegionId({businessId: authentication.business().uuid, regionId: businessUnit}).then(({teamId}) => {
        const usersAlreadyInTeam = selectedEmployees.filter((user) => user.teamIds.includes(teamId))
        const usersToAdd = selectedEmployees.filter((user) => !user.teamIds.includes(teamId))
        this.setState({usersAlreadyInTeam, usersToAdd})

        return Promise.all(
          usersToAdd.map((user) => teamService.addUserToTeam(teamId, user.uuid))
        ).then(() => {
          usersToAdd.map((user) => {
            const updatedTeamIds = user.teamIds.filter((team) => {
              return !businessUnits?.map(({uuid}) => uuid).find((id) => id === team)
            })
            const data = {
              uuid: user.uuid,
              teamIds: [...updatedTeamIds, teamId]
            }
            return userService.update({user: data})
          })
          this.setState({successfullyUpdatedEmployees: usersToAdd?.length})
        }).then(() => {
          onSave()
          this.toggleMoveBusinessUnitModal()
          this.toggleMoveBusinessUnitSuccessModal()
        })
      })
    })
    this.setState({submit})
  }

  updateTeam = (team) => {
    this.setState({team})
  }

  getSelectedTeamName = () => {
    const {team, teams} = this.state
    return team && teams && teams.find(({uuid}) => uuid === team)?.teamName
  }

  getSelectedBusinessUnit = () => {
    const {businessUnit, teamRegions} = this.state
    const team = teamRegions?.find(({regionId}) => regionId === businessUnit)?.team
    return teamName(team)
  }

  getTeamOptions = () => {
    const {teamId} = this.props
    const {teams} = this.state

    const teamOptions = teams.map((teamOption: Team) => ({value: teamOption.uuid, label: teamOption.teamName}))

    if (teamId) {
      return teamOptions.filter((teamOption: Team) => teamOption.value !== teamId)
    }
    return teamOptions

  }

  handleBusinessUnitSelect = (businessUnit: string, {isCountryNew}: {isCountryNew: boolean}) => {
    this.setState({businessUnit, isCountryNew})
  }

  getTeamTypeOptions = () => {
    const {formatMessage} = this.props
    return [
      {
        value: true,
        label: formatMessage({id: "admin.components.manageUsersPopUp.teamTypeOptions.primaryTeam.label"}),
        tooltip: formatMessage({id: "admin.components.manageUsersPopUp.teamTypeOptions.primaryTeam.tooltip"})
      },
      {value: false, label: formatMessage({id: "admin.components.manageUsersPopUp.teamTypeOptions.otherTeam.label"})}
    ]
  }

  render() {
    const {selectedEmployees, formatMessage} = this.props
    const {
      load,
      showMoveTeamModal,
      showMoveTeamSuccessModal,
      team,
      usersAlreadyInTeam,
      successfullyUpdatedEmployees,
      showMoveBusinessUnitModal,
      showMoveBusinessUnitSuccessModal,
      businessUnitConfirmation,
      businessUnit,
      isPrimaryTeamSelected,
      isCountryNew,
      submit} = this.state
    const employeesLength = selectedEmployees.length
    const isDeactivatedList = this.isDeactivatedList()

    return (
      <SideBarContext.Consumer>
        {({navWidth}) => (
          <PopUp navWidth={navWidth}>
            <Grid>
              {!!employeesLength &&
                <Flex alignItems="baseline" justifyContent="space-between" paddingTop={2} paddingBottom={2} marginRight={4}>
                  <H3 color={theme.colors.utility.ink}>
                    <FormattedMessage id="admin.components.manageUsersPopUp.header" values={{employeesLength}} />
                  </H3>
                  <div>
                    <Button marginRight={2} onClick={this.toggleMoveBusinessUnitModal}>
                      <FormattedMessage id="admin.components.manageUsersPopUp.button.moveCountry" />
                    </Button>
                    <Button onClick={this.toggleMoveTeamModal} marginRight={2}>
                      <FormattedMessage id="admin.components.manageUsersPopUp.button.moveTeam" />
                    </Button>
                    <Button destructive onClick={this.openDeactivatePopUp} marginRight={4}>
                      {isDeactivatedList ? (
                        <FormattedMessage id="admin.components.manageUsersPopUp.button.activate" />
                      ) : (
                        <FormattedMessage id="admin.components.manageUsersPopUp.button.deactivate" />
                      )}
                    </Button>
                  </div>
                </Flex>
              }
              <AreYouSurePopUp
                ref={(c) => {
                  this.deactivatePopup = c
                }}
                load={load}
                text={this.areYouSureText()}
                buttonText={
                  isDeactivatedList
                    ? formatMessage({id: "admin.components.manageUsersPopUp.button.buttonText.activate"})
                    : formatMessage({id: "admin.components.manageUsersPopUp.button.buttonText.deactivate"})
                }
                onSubmit={this.handleSubmit} />
              <MoveUsersModal
                title="admin.components.manageUsersPopUp.moveTeamPopup.title"
                proceedButtonText="admin.components.manageUsersPopUp.moveTeamPopup.proceedButtonText"
                selectedEmployees={selectedEmployees}
                show={showMoveTeamModal}
                submit={submit}
                onConfirm={this.handleMoveTeam}
                onHide={this.toggleMoveTeamModal}>
                <Row>
                  <Select
                    label={formatMessage({id: "admin.components.manageUsersPopUp.moveTeamPopup.select"})}
                    value={team}
                    onChange={this.updateTeam}
                    options={this.getTeamOptions()} />
                </Row>
                <Para flush>{formatMessage({id: "admin.components.manageUsersPopUp.moveTeamPopup.assignTeamPara"})}</Para>
                <Radio
                  name="teamType"
                  label={formatMessage({id: "admin.components.manageUsersPopUp.moveTeamPopup.assignTeamLabel"})}
                  value={isPrimaryTeamSelected}
                  options={this.getTeamTypeOptions()}
                  rowRenderer={ToolTipRenderer}
                  onChange={(value) => this.setState({isPrimaryTeamSelected: value})} />
              </MoveUsersModal>
              <MoveUsersModal
                title="admin.components.manageUsersPopUp.moveCountryPopup.title"
                proceedButtonText={isCountryNew
                  ? "admin.components.manageUsersPopUp.moveBusinessUnitPopup.addNewCountryproceedButtonText"
                  : "admin.components.manageUsersPopUp.moveBusinessUnitPopup.proceedButtonText"
                }
                selectedEmployees={selectedEmployees}
                show={showMoveBusinessUnitModal}
                submit={submit}
                onConfirm={this.handleMoveBusinessUnit}
                onHide={this.toggleMoveBusinessUnitModal}>
                <Row>
                  <BURegion
                    hideLabel
                    separateExisting
                    onChange={this.handleBusinessUnitSelect}
                    value={businessUnit}
                    source={MANAGE_USERS_BAR_SOURCE}
                    businessId={authentication.business().uuid} />
                  <Spacing marginBottom={1}>
                    <Checkbox
                      name="businessUnitConfirmation"
                      validation={Validation.bool().oneOf([true], "admin.components.manageUsersPopUp.moveBusinessUnitPopup.required")}
                      checked={businessUnitConfirmation}
                      onChange={this.toggleBusinessUnitConfirmation}>
                      <Label>
                        {formatMessage({id: "admin.components.manageUsersPopUp.moveCountryPopup.confirmation"})}
                      </Label>
                    </Checkbox>
                  </Spacing>
                </Row>
              </MoveUsersModal>
              <MoveUsersSuccessModal
                title="admin.components.manageUsersPopUp.moveTeamPopupSuccess.title"
                show={showMoveTeamSuccessModal}
                onToggle={this.toggleMoveTeamSuccessModal}
                successfullyUpdatedUsers={formatMessage({id: "admin.components.manageUsersPopUp.moveTeamPopupSuccess.para"},
                  {employees: successfullyUpdatedEmployees, team: this.getSelectedTeamName()})}
                usersAlreadyInTeam={usersAlreadyInTeam?.length
                  ? formatMessage({id: "admin.components.manageUsersPopUp.moveTeamPopupSuccess.usersNotAdded"},
                    {employees: usersAlreadyInTeam.length})
                  : undefined} />
              <MoveUsersSuccessModal
                title="admin.components.manageUsersPopUp.moveCountryPopupSuccess.title"
                show={showMoveBusinessUnitSuccessModal}
                onToggle={this.toggleMoveBusinessUnitSuccessModal}
                successfullyUpdatedUsers={formatMessage({id: "admin.components.manageUsersPopUp.moveBusinessUnitPopupSuccess.para"},
                  {employees: successfullyUpdatedEmployees, businessUnit: this.getSelectedBusinessUnit()})}
                usersAlreadyInTeam={usersAlreadyInTeam?.length
                  ? formatMessage({id: "admin.components.manageUsersPopUp.moveBusinessUnitPopupSuccess.usersNotAdded"},
                    {employees: usersAlreadyInTeam.length})
                  : undefined} />
            </Grid>
          </PopUp>
        )}
      </SideBarContext.Consumer>
    )
  }
}

export default injectIntl(ManageUsersBottomBar, {withRef: true})
